import { toMap } from '../../../../utils/commonUtils';
import { Recording, SeriesRecording, EpisodeRecording, SingleRecording } from '../../recordings/types/recording';
import { PlayablePlaybackOption } from '../types/playback-option';
import { ProgramSource } from '../types/sources';
import { EpisodeTitle } from '../types/title';

export function mergeEpisodes(target: EpisodeTitle[], ...episodes: EpisodeTitle[][]): EpisodeTitle[] {
  const flattenedEpisodes = episodes.flat();
  const episodeMap = toMap(
    flattenedEpisodes,
    (episode) => `S${episode.season.sequenceNumber}E${episode.episode.sequenceNumber}`,
  );

  if (flattenedEpisodes.length === 0) {
    return target;
  }

  for (const episode of target) {
    const key = `S${episode.season.sequenceNumber}E${episode.episode.sequenceNumber}`;
    const match = episodeMap.get(key);

    if (match === undefined) {
      episodeMap.set(key, episode);
      continue;
    }

    match.playbackOptions.push(...episode.playbackOptions);
  }

  return [...episodeMap.values()];
}

export function findRecordings(recordings: Recording[], option: PlayablePlaybackOption): Recording[] {
  return recordings.filter((rec) => {
    if (rec.type === 'series') {
      return 'seriesId' in option && option.seriesId && rec.seriesId === option.seriesId;
    }

    return rec.programId === option.platformTitleId || ('lifetimeId' in option && rec.lifetimeId === option.lifetimeId);
  });
}

export function findMatchingSeriesRecording(
  episodes: EpisodeTitle[],
  recordings: Recording[],
): SeriesRecording | undefined {
  let match: SeriesRecording | undefined;

  for (const episode of episodes) {
    for (const option of episode.playbackOptions) {
      if (option.serviceType !== 'program') {
        continue;
      }

      const recordingsForOptions = findRecordings(recordings, option);

      if (recordingsForOptions.length === 0) {
        continue;
      }

      const recording = recordingsForOptions.find((rec): rec is SeriesRecording => rec.type === 'series');

      if (recording === undefined) {
        continue;
      }

      if (recording.type === 'series') {
        match = recording;
        break;
      }
    }
  }

  return match;
}

/**
 * Returns the preferred recording for the source.
 * If searchSimilar is true, it will search for a similar recording.
 * A similar recording has a different programId, but the same lifetimeId.
 *
 * @param source The source to get the preferred recording for.
 * @param searchSimilar Whether to search for a similar recording.
 * @returns The preferred recording for the source.
 */
export function getPreferredRecording(
  source: Pick<ProgramSource, 'recordings' | 'platformTitleId' | 'lifetimeId'>,
  searchSimilar: boolean,
) {
  const recs = source.recordings.reduce<SingleRecording[]>((recordings, recording) => {
    if (recording.type === 'series') {
      const episode = getPreferredEpisodeRecording(recording.episodes, source, searchSimilar);
      return episode ? [...recordings, episode] : recordings;
    }

    return recording.programId === source.platformTitleId ? [...recordings, recording] : recordings;
  }, []);

  return recs.length > 0 ? recs[0] : undefined;
}

/**
 * Returns the preferred episode recording for the source.
 * If searchSimilar is true, it will search for a similar recording.
 * A similar recording has a different programId, but the same lifetimeId.
 *
 * @param episodes The episode recordings to get the preferred recording for.
 * @param source The source to get the preferred recording for.
 * @param searchSimilar Whether to search for a similar recording.
 * @returns The preferred episode recording for the source.
 */
export function getPreferredEpisodeRecording(
  episodes: EpisodeRecording[],
  source: Pick<ProgramSource, 'recordings' | 'platformTitleId' | 'lifetimeId'>,
  searchSimilar: boolean,
) {
  return episodes.find((t) => {
    if (!searchSimilar) {
      return t.programId === source.platformTitleId;
    }

    return t.programId === source.platformTitleId || ('lifetimeId' in source && t.lifetimeId === source.lifetimeId);
  });
}

export function getSeriesRecordingEpisodes(recordings: Recording[], seriesId: string, lifetimeId?: string) {
  return recordings.reduce<EpisodeRecording[]>((acc, recording) => {
    if (recording.type === 'series' && recording.seriesId === seriesId) {
      acc.push(...recording.episodes);
    }

    if (
      'series' in recording &&
      recording.series !== undefined &&
      (recording.series.id === seriesId || recording.lifetimeId === lifetimeId)
    ) {
      acc.push(recording);
    }

    return acc;
  }, []);
}
